import { useState, useRef, useEffect, KeyboardEvent, useCallback } from 'react';
import { Badge, Button, Card, TextButton } from '@safetyhub/ui';
import { useAuthStore } from '../../../stores/auth';
import { searchLegalBases, LegalBasis as ApiLegalBasis } from '../../../lib/measures';
import { useDebounce } from '../../../hooks/useDebounce';
import { LegalGroupList } from './LegalGroupList';
import { formatLegalBasisBody, type RefinedLegalBasis } from '@safetyhub/shared';

/** 내부 alias — RefinedLegalBasis와 동일 */
type LegalBasis = RefinedLegalBasis;

/**
 * 검색 결과 1건의 본문 텍스트.
 *
 * 조문 본문이 `law_articles.content`가 아니라 `law_paragraphs`에만 있는 조문(전기안전관리법
 * 제43조 등)은 summary가 비어 있고 항만 온다. 표시 정본(formatLegalBasisBody)이 그 항들을
 * ①②로 합성해 주므로 summary 대신 이걸 읽는다.
 *
 * 다만 정본은 아무 원천도 없을 때 title로 fallback하는데, 여기서는 제목이 이미 머리줄에
 * 있어 중복이라 버린다 — shared formatLegalBasisArticleRows의 bodyIsTitleFallback과 같은 규칙.
 */
function legalBasisBodyText(legal: ApiLegalBasis): string {
  const body = formatLegalBasisBody(legal).trim();
  return body === (legal.title ?? '').trim() ? '' : body;
}

/** 변경 상태 타입 */
type ChangeStatus = 'unchanged' | 'added' | 'removed';

interface LegalChangeInfo {
  value: LegalBasis;
  status: ChangeStatus;
}

interface EditableLegalListProps {
  /** 관련법규 목록 */
  legalBasis: LegalBasis[];
  /** 관련법규 추가 핸들러 (선택된 대책들과 함께) */
  onAdd: (legal: LegalBasis, selectedMeasures?: string[]) => Promise<void>;
  /** 관련법규 삭제 핸들러 */
  onRemove: (index: number) => Promise<void>;
  /** 읽기 전용 */
  disabled?: boolean;
  /** 변경 상태 정보 (원본과 비교) */
  changes?: LegalChangeInfo[];
  /** 카테고리 (지식베이스 검색 필터용) */
  category?: string;
  /** 중주제 (지식베이스 검색 필터용) */
  subCategory?: string | null;
  /** 발견사항의 대책 목록 (피드백용 연결) */
  measures?: string[];
  /** 헤더 숨김 (테이블 모드에서 좌측 라벨로 대체) */
  hideLabel?: boolean;
}

export function EditableLegalList({
  legalBasis,
  onAdd,
  onRemove,
  disabled = false,
  changes,
  // category와 subCategory는 더 이상 검색 필터로 사용하지 않음
  // 전체 법규 검색 후 대책 선택 단계에서 연결
  category: _category,
  subCategory: _subCategory,
  measures = [],
  hideLabel = false,
}: EditableLegalListProps) {
  // knowledge backend endpoint는 companyId 기반 — currentMembership에서 추출
  const companyId = useAuthStore((state) => state.currentMembership?.companyId ?? null);
  const [isAdding, setIsAdding] = useState(false);
  const [inputMode, setInputMode] = useState<'search' | 'manual'>('search');
  const [newLegal, setNewLegal] = useState<LegalBasis>({
    type: '',
    article: '',
    summary: '',
    url: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const typeInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 지식베이스 검색 관련
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ApiLegalBasis[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 300);

  // 대책 선택 관련 (법규 선택 후 대책 연결)
  const [pendingLegal, setPendingLegal] = useState<LegalBasis | null>(null);
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());

  // 지식베이스에서 법규 검색 (전체 법규 대상, 카테고리 필터 없음)
  const fetchSuggestions = useCallback(async (query: string) => {
    if (!companyId) return;

    setIsSearching(true);
    try {
      const results = await searchLegalBases(companyId, {
        search: query || undefined,
        // 카테고리/서브카테고리 필터 없이 전체 법규 검색
        // 추가된 법규는 대책 선택 단계에서 연결됨
        limit: 30,
      });

      // 이미 추가된 법규 제외
      const existingKeys = new Set(
        legalBasis.map((lb) => `${lb.type}|${lb.article || ''}`),
      );
      const filtered = results.filter(
        (r) => !existingKeys.has(`${r.type}|${r.article || ''}`),
      );

      setSuggestions(filtered);
    } catch (err) {
      console.error('Failed to search legal bases:', err);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, [companyId, legalBasis]);

  // 검색어 변경 시 자동 검색
  useEffect(() => {
    if (isAdding && inputMode === 'search' && companyId && !pendingLegal) {
      fetchSuggestions(debouncedSearch);
    }
  }, [debouncedSearch, isAdding, inputMode, companyId, fetchSuggestions, pendingLegal]);

  useEffect(() => {
    if (isAdding && !pendingLegal) {
      if (inputMode === 'manual' && typeInputRef.current) {
        typeInputRef.current.focus();
      } else if (inputMode === 'search' && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [isAdding, inputMode, pendingLegal]);

  const handleStartAdd = () => {
    if (disabled) return;
    setIsAdding(true);
    setInputMode(companyId ? 'search' : 'manual');
    setNewLegal({ type: '', article: '', summary: '', url: '' });
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(true);
    setPendingLegal(null);
    setSelectedMeasures(new Set());
    setError(null);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setInputMode('search');
    setNewLegal({ type: '', article: '', summary: '', url: '' });
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setPendingLegal(null);
    setSelectedMeasures(new Set());
    setError(null);
  };

  // 대책 선택 단계로 이동
  const proceedToMeasureSelection = (legal: LegalBasis) => {
    if (measures.length === 0) {
      // 대책이 없으면 바로 추가 (피드백용 대책 연결 없음)
      confirmAddLegal(legal, []);
    } else {
      // 대책이 있으면 선택 UI 표시
      setPendingLegal(legal);
      setSelectedMeasures(new Set(measures)); // 기본적으로 모든 대책 선택
    }
  };

  // 최종 법규 추가
  const confirmAddLegal = async (legal: LegalBasis, selectedMeasureList: string[]) => {
    setIsSaving(true);
    setError(null);
    try {
      await onAdd(legal, selectedMeasureList.length > 0 ? selectedMeasureList : undefined);

      // 추가 완료 후 입력 폼 닫기
      handleCancelAdd();
    } catch (err) {
      setError(err instanceof Error ? err.message : '추가에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSuggestion = (apiLegal: ApiLegalBasis) => {
    // 검색 결과(매칭 메타 포함)를 그대로 저장하지 않고 목록에 필요한 값만 추린다 —
    // 다만 본문은 여기서 확정해야 한다. summary만 옮기면 본문이 항에만 있는 조문이
    // 빈 요약으로 저장돼 제목 줄만 남는다(저장본은 결재 증빙이라 구조가 아닌 확정 텍스트로).
    const legal: LegalBasis = {
      type: apiLegal.type,
      article: apiLegal.article,
      paragraphNo: apiLegal.paragraphNo,
      title: apiLegal.title,
      summary: legalBasisBodyText(apiLegal),
      url: apiLegal.url,
      regulationId: apiLegal.regulationId,
    };
    proceedToMeasureSelection(legal);
  };

  const handleAdd = () => {
    const type = newLegal.type.trim();
    const summary = newLegal.summary.trim();

    if (!type || !summary) {
      setError('법규 종류와 내용 요약은 필수입니다.');
      return;
    }

    const legal: LegalBasis = {
      type,
      article: newLegal.article?.trim() || undefined,
      summary,
      url: newLegal.url?.trim() || undefined,
    };
    proceedToMeasureSelection(legal);
  };

  const handleConfirmWithMeasures = async () => {
    if (!pendingLegal) return;
    await confirmAddLegal(pendingLegal, Array.from(selectedMeasures));
  };

  const handleMeasureToggle = (measure: string) => {
    setSelectedMeasures(prev => {
      const next = new Set(prev);
      if (next.has(measure)) {
        next.delete(measure);
      } else {
        next.add(measure);
      }
      return next;
    });
  };

  const handleSelectAllMeasures = () => {
    setSelectedMeasures(new Set(measures));
  };

  const handleDeselectAllMeasures = () => {
    setSelectedMeasures(new Set());
  };

  const handleRemove = async (index: number) => {
    if (disabled || removingIndex !== null) return;

    setRemovingIndex(index);
    try {
      await onRemove(index);
    } catch (err) {
      setError(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    } finally {
      setRemovingIndex(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancelAdd();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="mb-2">
      {/* 헤더 */}
      {!hideLabel && (
        <div className="text-xs font-medium text-ink-600 mb-1.5 flex items-center gap-1">
          관련 법규
        </div>
      )}

      {/* 목록 */}
      <LegalGroupList
        legalBasis={legalBasis}
        changes={changes}
        isAdding={isAdding}
        disabled={disabled}
        removingIndex={removingIndex}
        onStartAdd={handleStartAdd}
        onRemove={handleRemove}
      />

      {/* 추가 버튼 (항목 하단) */}
      {!disabled && !isAdding && (
        <button
          onClick={handleStartAdd}
          className="text-xs text-brand-600 hover:text-brand-800 hover:bg-brand-50 px-2 py-1 rounded transition-colors mt-1"
        >
          + 추가
        </button>
      )}

      {/* 입력 폼 */}
      {isAdding && (
        <Card padding="sm" surface="soft" elevation="none" className="mt-2">
          {/* 대책 선택 화면 */}
          {pendingLegal ? (
            <div>
              {/* 선택된 법규 표시 */}
              <div className="mb-3 p-2 bg-brand-50 border border-brand-200 rounded-card-sm">
                <div className="text-xs font-medium text-brand-800">
                  {pendingLegal.type} {pendingLegal.article}
                </div>
                {pendingLegal.summary && (
                  <div className="text-xs text-brand-600 mt-0.5">{pendingLegal.summary}</div>
                )}
              </div>

              {/* 대책 선택 */}
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-ink-700">연결할 대책 선택</span>
                  <div className="flex gap-1">
                    <TextButton onClick={handleSelectAllMeasures} className="text-[10px]">
                      전체선택
                    </TextButton>
                    <span className="text-ink-300">|</span>
                    <TextButton
                      tone="neutral"
                      onClick={handleDeselectAllMeasures}
                      className="text-[10px]"
                    >
                      전체해제
                    </TextButton>
                  </div>
                </div>
                <Card
                  padding="none"
                  radius="sm"
                  elevation="none"
                  className="max-h-32 overflow-y-auto"
                >
                  {measures.map((measure, idx) => (
                    <label
                      key={idx}
                      className="flex items-start gap-2 px-2 py-1.5 hover:bg-surface-hover cursor-pointer border-b border-line-soft last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMeasures.has(measure)}
                        onChange={() => handleMeasureToggle(measure)}
                        className="mt-0.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-xs text-ink-700 flex-1">{measure}</span>
                    </label>
                  ))}
                </Card>
                <div className="text-[10px] text-ink-400 mt-1">
                  {selectedMeasures.size}개 선택됨 · 선택된 대책과 법규가 연결되어 피드백 자료로 활용됩니다
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end gap-1">
                <button
                  onClick={() => {
                    setPendingLegal(null);
                    setSelectedMeasures(new Set());
                  }}
                  disabled={isSaving}
                  className="text-xs text-ink-500 hover:text-ink-700 px-2 py-1"
                >
                  이전
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  onClick={handleConfirmWithMeasures}
                  disabled={isSaving}
                >
                  {isSaving ? '추가 중...' : '추가'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* 모드 전환 탭 (companyId가 있을 때만) */}
              {companyId && (
                <div className="flex gap-1 mb-2 border-b border-line pb-2">
                  <button
                    onClick={() => setInputMode('search')}
                    className={`text-xs px-2 py-1 rounded ${
                      inputMode === 'search'
                        ? 'bg-brand-500 text-white'
                        : 'bg-ink-200 text-ink-600 hover:bg-ink-300'
                    }`}
                  >
                    기존 법규에서 선택
                  </button>
                  <button
                    onClick={() => setInputMode('manual')}
                    className={`text-xs px-2 py-1 rounded ${
                      inputMode === 'manual'
                        ? 'bg-brand-500 text-white'
                        : 'bg-ink-200 text-ink-600 hover:bg-ink-300'
                    }`}
                  >
                    직접 입력
                  </button>
                </div>
              )}

              {/* 검색 모드 */}
              {inputMode === 'search' && companyId && (
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') handleCancelAdd();
                    }}
                    disabled={isSaving}
                    placeholder="법규 검색 (예: 산업안전보건법, 추락)"
                    className="w-full px-2 py-1.5 text-sm border rounded-control border-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  {isSearching && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <div className="animate-spin w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full" />
                    </div>
                  )}

                  {/* 검색 결과 드롭다운 */}
                  {showSuggestions && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-line rounded-card shadow-card max-h-60 overflow-y-auto">
                      {suggestions.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-ink-500">
                          {isSearching ? '검색 중...' : searchQuery ? '검색 결과가 없습니다' : '검색어를 입력하거나 목록에서 선택하세요'}
                        </div>
                      ) : (
                        suggestions.map((legal, idx) => {
                          const bodyText = legalBasisBodyText(legal);
                          return (
                          <button
                            key={`${legal.type}-${legal.article}-${idx}`}
                            onClick={() => handleSelectSuggestion(legal)}
                            disabled={isSaving}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-line-soft last:border-b-0 disabled:opacity-50"
                          >
                            <div className="font-medium text-brand-800 flex items-center gap-1">
                              {legal.type} {legal.article}
                              {legal.title && (
                                <span className="text-ink-500 font-normal">({legal.title})</span>
                              )}
                              {legal.regulationId && (
                                <Badge tone="emerald" shape="rounded" size="sm">
                                  공식
                                </Badge>
                              )}
                            </div>
                            {bodyText && (
                              <div className="text-ink-600 mt-0.5 line-clamp-2 whitespace-pre-line">
                                {bodyText}
                              </div>
                            )}
                          </button>
                          );
                        })
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-ink-400">목록에서 선택하여 추가</span>
                    <button
                      onClick={handleCancelAdd}
                      disabled={isSaving}
                      className="text-xs text-ink-500 hover:text-ink-700 px-2 py-0.5"
                    >
                      닫기
                    </button>
                  </div>
                </div>
              )}

              {/* 직접 입력 모드 */}
              {(inputMode === 'manual' || !companyId) && (
                <>
                  <div className="space-y-2">
                    {/* 법규 종류 */}
                    <div className="flex gap-2">
                      <input
                        ref={typeInputRef}
                        type="text"
                        value={newLegal.type}
                        onChange={(e) => setNewLegal({ ...newLegal, type: e.target.value })}
                        onKeyDown={handleKeyDown}
                        disabled={isSaving}
                        placeholder="법규 종류 (예: 산업안전보건법)"
                        className="flex-1 px-2 py-1 text-sm border rounded-control border-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                      <input
                        type="text"
                        value={newLegal.article}
                        onChange={(e) => setNewLegal({ ...newLegal, article: e.target.value })}
                        onKeyDown={handleKeyDown}
                        disabled={isSaving}
                        placeholder="조항 (예: 제42조)"
                        className="w-32 px-2 py-1 text-sm border rounded-control border-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </div>

                    {/* 내용 요약 */}
                    <input
                      type="text"
                      value={newLegal.summary}
                      onChange={(e) => setNewLegal({ ...newLegal, summary: e.target.value })}
                      onKeyDown={handleKeyDown}
                      disabled={isSaving}
                      placeholder="내용 요약 (필수)"
                      className="w-full px-2 py-1 text-sm border rounded-control border-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />

                    {/* URL (선택) */}
                    <input
                      type="url"
                      value={newLegal.url}
                      onChange={(e) => setNewLegal({ ...newLegal, url: e.target.value })}
                      onKeyDown={handleKeyDown}
                      disabled={isSaving}
                      placeholder="참조 URL (선택)"
                      className="w-full px-2 py-1 text-sm border rounded-control border-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>

                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-ink-400">
                      {measures.length > 0 ? '다음 단계에서 대책을 선택합니다' : 'Ctrl+Enter로 저장, Esc로 취소'}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={handleCancelAdd}
                        disabled={isSaving}
                        className="text-xs text-ink-500 hover:text-ink-700 px-2 py-0.5"
                      >
                        취소
                      </button>
                      <Button
                        variant="primary"
                        size="xs"
                        onClick={handleAdd}
                        disabled={isSaving || !newLegal.type.trim() || !newLegal.summary.trim()}
                      >
                        {measures.length > 0 ? '다음' : '저장'}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="text-xs text-red-500 mt-1">{error}</div>
      )}
    </div>
  );
}

export default EditableLegalList;
